import { useEffect, useState } from "react";
import { api, eur, formatApiErrorDetail, RATE_CODES, RATE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PencilLine, ChevronRight, ChevronDown } from "lucide-react";

export default function Corrections() {
  const { canWrite } = useAuth();
  const [corrections, setCorrections] = useState([]);
  const [entries, setEntries] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [open, setOpen] = useState(false);
  const [origId, setOrigId] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [rates, setRates] = useState({ "0": "", "6": "", "21": "", "E": "" });
  const [reason, setReason] = useState("");
  const [invoice, setInvoice] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/corrections").then((r) => setCorrections(r.data));
    api.get("/journal").then((r) => setEntries(r.data));
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!origId || !reason) return toast.error("Kies boeking en geef reden");
    setSaving(true);
    try {
      const correction_by_rate = {};
      RATE_CODES.forEach((r) => (correction_by_rate[r] = parseFloat(rates[r]) || 0));
      await api.post("/corrections", { original_entry_id: origId, date, correction_by_rate, reason, invoice_number: invoice });
      toast.success("Correctie geboekt — origineel blijft bewaard");
      setOpen(false); setRates({ "0": "", "6": "", "21": "", "E": "" }); setReason(""); setInvoice("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="corrections-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">Correcties</h1>
          <p className="text-sm text-muted-foreground mt-1">Correcties zijn nieuwe gedateerde boekingen die naar het origineel verwijzen (+/- bedrag).</p>
        </div>
        {canWrite && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="open-correction"><PencilLine className="h-4 w-4 mr-2" /> Nieuwe correctie</Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader><DialogTitle className="font-heading">Correctieboeking</DialogTitle><DialogDescription>Een correctie is een nieuwe gedateerde boeking (+ of −) die naar het origineel verwijst.</DialogDescription></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label>Originele boeking</Label>
                  <Select value={origId} onValueChange={setOrigId}>
                    <SelectTrigger data-testid="correction-original"><SelectValue placeholder="Kies boeking" /></SelectTrigger>
                    <SelectContent>
                      {entries.map((e) => (
                        <SelectItem key={e.id} value={e.id}>{e.date} · {e.location_name} · {eur(e.vat.total_gross)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cd">Correctiedatum</Label>
                  <Input id="cd" type="date" data-testid="correction-date" value={date} onChange={(e) => setDate(e.target.value)} />
                </div>
                <div>
                  <Label className="text-sm">Correctiebedrag per tarief (incl. BTW, + of −)</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-1.5">
                    {RATE_CODES.map((r) => (
                      <div key={r} className="space-y-1">
                        <Label className="text-xs text-muted-foreground">{RATE_LABELS[r]}</Label>
                        <Input type="number" step="0.01" data-testid={`correction-rate-${r}`} value={rates[r]}
                               onChange={(e) => setRates({ ...rates, [r]: e.target.value })} placeholder="0,00" />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="inv">Factuurnummer (indien van toepassing)</Label>
                  <Input id="inv" data-testid="correction-invoice" value={invoice} onChange={(e) => setInvoice(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="rsn">Reden van correctie</Label>
                  <Textarea id="rsn" data-testid="correction-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={submit} disabled={saving} data-testid="submit-correction">{saving ? "Bezig…" : "Correctie boeken"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Correctiedatum</TableHead>
                <TableHead>Origineel</TableHead>
                <TableHead>Vestiging</TableHead>
                <TableHead className="text-right">Correctie incl.</TableHead>
                <TableHead className="text-right">BTW</TableHead>
                <TableHead>Factuur</TableHead>
                <TableHead>Reden</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {corrections.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">Nog geen correcties.</TableCell></TableRow>
              )}
              {corrections.map((c) => {
                const isOpen = !!expanded[c.id];
                const lines = (c.correction?.lines || []).filter((l) => Math.abs(l.gross || 0) > 0.005 || Math.abs(l.base || 0) > 0.005);
                return (
                <>
                <TableRow key={c.id} data-testid={`correction-row-${c.id}`} className="cursor-pointer hover:bg-muted/40"
                          onClick={() => setExpanded((p) => ({ ...p, [c.id]: !p[c.id] }))}>
                  <TableCell className="py-2" data-testid={`correction-toggle-${c.id}`}>
                    {isOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                  </TableCell>
                  <TableCell className="font-mono text-sm">{c.date}</TableCell>
                  <TableCell className="font-mono text-sm">{c.original_date}</TableCell>
                  <TableCell>{c.location_name}</TableCell>
                  <TableCell className={`text-right tabular-nums font-semibold ${c.correction.total_gross < 0 ? "text-destructive" : "text-emerald-600"}`}>
                    {eur(c.correction.total_gross)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">{eur(c.correction.total_vat)}</TableCell>
                  <TableCell className="text-sm">{c.invoice_number || "—"}</TableCell>
                  <TableCell className="text-sm max-w-xs truncate">{c.reason}</TableCell>
                </TableRow>
                {isOpen && (
                  <TableRow key={c.id + "-detail"} data-testid={`correction-detail-${c.id}`} className="bg-muted/30">
                    <TableCell></TableCell>
                    <TableCell colSpan={7} className="py-3">
                      <div className="text-xs font-medium text-muted-foreground mb-2">Verdeling per btw-tarief (incl. btw, + of −)</div>
                      <table className="w-full max-w-2xl text-sm">
                        <thead>
                          <tr className="text-xs text-muted-foreground border-b">
                            <th className="text-left font-medium py-1">Tarief</th>
                            <th className="text-right font-medium py-1">Exclusief</th>
                            <th className="text-right font-medium py-1">BTW</th>
                            <th className="text-right font-medium py-1">Inclusief</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lines.length === 0 && (
                            <tr><td colSpan={4} className="py-2 text-muted-foreground">Geen bedragen.</td></tr>
                          )}
                          {lines.map((l) => (
                            <tr key={l.code} className="border-b last:border-0">
                              <td className="py-1">{l.label || RATE_LABELS[l.code] || l.code}</td>
                              <td className={`py-1 text-right tabular-nums ${l.base < 0 ? "text-destructive" : ""}`}>{eur(l.base)}</td>
                              <td className={`py-1 text-right tabular-nums ${l.vat < 0 ? "text-destructive" : ""}`}>{eur(l.vat)}</td>
                              <td className={`py-1 text-right tabular-nums font-medium ${l.gross < 0 ? "text-destructive" : ""}`}>{eur(l.gross)}</td>
                            </tr>
                          ))}
                          <tr className="font-semibold border-t">
                            <td className="py-1">Totaal</td>
                            <td className="py-1 text-right tabular-nums">{eur(c.correction.total_base)}</td>
                            <td className="py-1 text-right tabular-nums">{eur(c.correction.total_vat)}</td>
                            <td className="py-1 text-right tabular-nums">{eur(c.correction.total_gross)}</td>
                          </tr>
                        </tbody>
                      </table>
                      <div className="text-xs text-muted-foreground mt-2">Deze correctie telt mee op de originele dag <span className="font-mono">{c.original_date}</span> (dagboek, grootboek én kasboek).</div>
                    </TableCell>
                  </TableRow>
                )}
                </>
              );})}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
